import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsCardContainer from '../../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import FileDropzone from '../../../../../shared/components/v3/FileDropzone/FileDropzone';
import BdsDivider from '../../../../../shared/components/v3/Globals/BdsDivider/BdsDivider';
import Toast from '../../../../../shared/components/v3/Toast/Toast';
import { DOWNLOAD_BLACK_ICON } from '../../../../../shared/constants/images';
import { SUBSCRIPTION_FILE_UPLOADER_VALIDATORS } from '../../../../../shared/constants/manageCollaborators';
import { PLAN_TYPE_FR } from '../../../../../shared/constants/plan';
import {
  CLICK_ACTION,
  COLLABORATOR_UPLOAD_METHOD_CHOICE_DICT,
  COLLABORATORS_MANAGEMENT_ERROR_FILE,
  ERROR_ACTION,
  FILE_SIZE_ERROR,
  GENERIC_ERROR,
  INVITE_COLLABORATORS_CONFIRM_NEW_RECORDS,
  TABLE_ERROR
} from '../../../../../shared/constants/tagManager';
import {
  COLLABORATOR_LIMIT_ERROR_TOAST_NE,
  DOWNLOAD_TEMPLATE_ERROR_TOAST,
  DOWNLOAD_TEMPLATE_SUCCESS_TOAST,
  GENERIC_ERROR_TOAST
} from '../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import type {
  FileState,
  IMemberByFileData,
  IMemberByFileToUpload,
  ISubscriptionByFile,
  IValidateFileError,
  IValidateFileSuccess,
  IRequestSubscriptionFile,
  ToastState,
  UIState
} from '../../../../../shared/interfaces/manageCollaborators';
import type { IToast } from '../../../../../shared/interfaces/toast';
import Companies from '../../../../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../../../shared/translations/manageCollaborators';
import { downloadFile } from '../../../../../shared/utils/utils';
import SubscriptionCollaboratorsHeadingContainer from '../HeadingContainer/SubscriptionCollaboratorsHeadingContainer';
import InformativeModal from './sections/InformativeModal/InformativeModal';
import SubscriptionByFileTable from './sections/TableWithErrors/SubscriptionByFileTable';
import { mapMemberByFileDataToMemberByFileToUpload } from './utils/mapper';

import './SubscriptionByFile.scss';

const SubscriptionByFile = ({ planSelected, setEmailsToUpload, emailsData, nextStep }: ISubscriptionByFile) => {
  const [fileState, setFileState] = useState<FileState>({
    base64: '',
    isValid: false,
    showCorrectedMessage: false
  });
  const [uiState, setUiState] = useState<UIState>({ loading: false, resetDropzone: false });
  const [toast, setToast] = useState<ToastState>({ open: false, data: undefined });
  const [showTableErrors, setShowTableErrors] = useState<boolean>(false);
  const [members, setMembers] = useState<IMemberByFileToUpload[]>([]);
  const [membersValids, setMembersValids] = useState<IMemberByFileToUpload[]>([]);
  const [membersWithError, setMembersWithError] = useState<IValidateFileError>();
  const [flowState, setFlowState] = useState({ failed: false, success: false });
  const { company } = useContext(CompanyContext);

  const { t } = useTranslation();

  const isFR = useMemo(() => planSelected.type === PLAN_TYPE_FR, [planSelected.type]);

  const disabledButton = useMemo(() => {
    if (flowState.failed) {
      return !fileState.isValid || uiState.loading;
    } else {
      return !fileState.isValid || uiState.loading || !flowState.success;
    }
  }, [fileState.isValid, flowState.failed, flowState.success, uiState.loading]);

  const showToast = useCallback((toastConfig: IToast) => {
    setToast({
      open: true,
      data: toastConfig
    });
  }, []);

  const showLimitRowsToast = useCallback(() => {
    showToast(COLLABORATOR_LIMIT_ERROR_TOAST_NE);
    setFileState((prev) => ({
      ...prev,
      isValid: false,
      showCorrectedMessage: false
    }));
    setUiState((prev) => ({
      ...prev,
      resetDropzone: !prev.resetDropzone
    }));
  }, [showToast]);

  const validateMembersToLoad = useCallback(
    async (members: IValidateFileSuccess[], isSecondPayroll: boolean, requestId: string | null) => {
      try {
        const emails = members.map((member) => member.email);
        const response = await Companies.validateMembers(company.id, emails);
        if (response.error) {
          showLimitRowsToast();
          return;
        }
        const emailsToUpload = members.filter((member) => response.missingEmails.includes(member.email));
        setEmailsToUpload({
          emailsToUpload: emailsToUpload,
          emailsOmmited: response.existingEmails,
          requestId: requestId
        });

        if (isSecondPayroll) {
          TagManagerService.pushEvent(
            CLICK_ACTION,
            INVITE_COLLABORATORS_CONFIRM_NEW_RECORDS,
            COLLABORATOR_UPLOAD_METHOD_CHOICE_DICT.uploadByFile
          );
          nextStep();
        }

        setFileState((prev) => ({
          ...prev,
          isValid: true,
          showCorrectedMessage: true
        }));

        setFlowState((prev) => ({ ...prev, success: true }));
      } catch {
        setFileState((prev) => ({ ...prev, isValid: false }));
        setFlowState((prev) => ({ ...prev, success: false }));
      }
    },
    [company.id]
  );

  const validateFile = useCallback(
    async (args: IRequestSubscriptionFile) => {
      if (!args) return;

      setUiState((prev) => ({ ...prev, loading: true }));
      setMembersWithError(undefined);
      setMembers([]);
      setMembersValids([]);
      try {
        const response = await Companies.validateSubscriptionFile({
          companyId: company.id,
          planId: planSelected.id,
          countryCode: planSelected.countryCode,
          isFR: isFR,
          records: args.members,
          base64: args.fileBase64
        });

        if (response.success) {
          const result = response.data as IValidateFileSuccess[];
          await validateMembersToLoad(result, args.members ? args.members.length > 0 : false, response.requestId);
        } else {
          const result = response.data as IValidateFileError;
          const membersValids = result.valids;
          setShowTableErrors(true);
          setMembersWithError(result);
          setMembersValids(membersValids);
          TagManagerService.pushEvent(ERROR_ACTION, COLLABORATORS_MANAGEMENT_ERROR_FILE, TABLE_ERROR);
        }
      } catch (error: any) {
        if (error?.response?.data?.code === 54) {
          TagManagerService.pushEvent(ERROR_ACTION, COLLABORATORS_MANAGEMENT_ERROR_FILE, FILE_SIZE_ERROR);
          showLimitRowsToast();
        } else {
          TagManagerService.pushEvent(ERROR_ACTION, COLLABORATORS_MANAGEMENT_ERROR_FILE, GENERIC_ERROR);
          showToast(GENERIC_ERROR_TOAST);
          setUiState((prev) => ({
            ...prev,
            resetDropzone: !prev.resetDropzone
          }));
        }
      } finally {
        setUiState((prev) => ({ ...prev, loading: false }));
      }
    },
    [company.id, planSelected.id, planSelected.countryCode, isFR, validateMembersToLoad, showLimitRowsToast, showToast]
  );

  const handleTemplateDownload = useCallback(async () => {
    try {
      const response = await Companies.downloadSubscriptionTemplate(company.id, planSelected.countryCode, String(isFR));

      if (response?.url) {
        downloadFile(response.url);
        showToast(DOWNLOAD_TEMPLATE_SUCCESS_TOAST);
      }
    } catch {
      showToast(DOWNLOAD_TEMPLATE_ERROR_TOAST);
    }
  }, [company.id, planSelected.countryCode, isFR, showToast]);

  const handleFileUpdate = useCallback((base64File: string) => {
    if (base64File) {
      setFileState((prev) => ({ ...prev, isValid: true, base64: base64File }));
    } else {
      setFileState((prev) => ({
        ...prev,
        base64: '',
        error: '',
        isValid: false,
        showCorrectedMessage: false
      }));
    }
  }, []);

  const handleCloseToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  const handleSetMemberToUpload = (members: IMemberByFileData[]) => {
    const membersToUpload = members.map(mapMemberByFileDataToMemberByFileToUpload);
    setMembers(membersToUpload);
  };

  const membersCounter = useCallback(() => {
    if (showTableErrors) {
      return members.length + membersValids.length;
    }

    return emailsData.emailsToUpload.length;
  }, [emailsData, members, membersValids, showTableErrors]);

  useEffect(() => {
    if (fileState.base64) {
      validateFile({ fileBase64: fileState.base64 });
    }
  }, [fileState.base64, validateFile]);

  const handleNextStep = () => {
    if (flowState.failed) {
      validateFile({ members: [...membersValids, ...members] });
    } else {
      TagManagerService.pushEvent(
        CLICK_ACTION,
        INVITE_COLLABORATORS_CONFIRM_NEW_RECORDS,
        COLLABORATOR_UPLOAD_METHOD_CHOICE_DICT.uploadByFile
      );
      nextStep();
    }
  };

  return (
    <>
      <div className="subscription-by-file flex flex-col w-full" data-experience="new">
        <div className="flex flex-row justify-between items-center">
          <h3 className="template-title">
            {t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.title)}
          </h3>
          <InformativeModal isFR={isFR} />
        </div>
        <BdsCardContainer paddingSize="padding-l" borderRadiusSize="radius-l" className="text-area-container">
          <SubscriptionCollaboratorsHeadingContainer
            emailsCounter={membersCounter()}
            loading={uiState.loading}
            planSelected={planSelected}
          />
          <BdsDivider size="s" color="subtle" />
          {showTableErrors ? (
            <SubscriptionByFileTable
              planCountryCode={planSelected.countryCode}
              isFR={isFR}
              membersWithError={membersWithError?.invalids ?? []}
              setMemberToUpload={handleSetMemberToUpload}
              setReadyToUpload={(ready) => setFlowState((prev) => ({ ...prev, failed: ready }))}
            />
          ) : (
            <div className="flex flex-row w-full mt-10">
              <div className="subscription-template flex flex-column items-start gap-6">
                <div className="subscription-template-title">
                  {t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.downloadTemplate)}
                </div>
                <BdsButton action={handleTemplateDownload} variant="secondary" size="m" iconLeft={DOWNLOAD_BLACK_ICON}>
                  {t(
                    TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile
                      .downloadTemplateButton
                  )}
                </BdsButton>
              </div>
              <div className="subscription-file-dropzone">
                <FileDropzone
                  updateBase64={handleFileUpdate}
                  reset={uiState.resetDropzone}
                  loading={uiState.loading}
                  validator={SUBSCRIPTION_FILE_UPLOADER_VALIDATORS}
                  inputTitle={t(TRANSLATIONS_GLOBAL.fileDropzone.fileTitle)}
                  correctedFileMessage={fileState.showCorrectedMessage}
                />
              </div>
            </div>
          )}
        </BdsCardContainer>
        <div className="manage-collaborators-button">
          <BdsButton variant="primary" size="l" action={handleNextStep} disabled={disabledButton}>
            {t(TRANSLATION_MANAGE_COLLABORATORS.buttonNext)}
          </BdsButton>
        </div>
      </div>
      <Toast
        title={t(toast.data?.title || '')}
        description={t(toast.data?.description || '')}
        icon={toast.data?.icon || 'error'}
        open={toast.open}
        button={toast.data?.button}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </>
  );
};

export default SubscriptionByFile;
